/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { LogoIcon } from 'components/icons';
import { routesPaths } from 'constants/routesPaths';
import './PreviewHomePage.scss';

const STATUS_OPTIONS = [
  { value: 'ready', label: 'Ready for review' },
  { value: 'iteration', label: 'In iteration' },
  { value: 'design', label: 'Design review' },
  { value: 'draft', label: 'Draft' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'shipped', label: 'Shipped' }
];

const STATUS_STORAGE_PREFIX = 'identifi.preview.dashboardStatus.';

function readStoredStatus(key) {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(`${STATUS_STORAGE_PREFIX}${key}`);
    if (!raw) return null;
    const match = STATUS_OPTIONS.find(o => o.value === raw);
    return match ? match.value : null;
  } catch (_e) {
    return null;
  }
}

function writeStoredStatus(key, value) {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(`${STATUS_STORAGE_PREFIX}${key}`, value);
  } catch (_e) {
    /* ignore */
  }
}

const DASHBOARDS = [
  {
    key: 'creator',
    label: 'Creator',
    persona: 'Celebrity / Creator workspace',
    status: 'Ready for review',
    statusTone: 'ready',
    tagline: 'Revenue, compliance, audience, and payout operations.',
    summary:
      'The role-gated creator cockpit — finance, content pipeline, live event center, fan CRM, ad manager, and Connect payouts.',
    entryRoute: routesPaths.creatorDashboard,
    screens: [
      {
        to: routesPaths.creatorDashboard,
        label: 'Overview',
        note: 'KPIs, next payout, risk flags'
      },
      {
        to: routesPaths.creatorFinance,
        label: 'Revenue & payouts',
        note: 'Waterfall, Stripe Connect, reserve'
      },
      {
        to: routesPaths.creatorContent,
        label: 'Content',
        note: 'Narratives, originals, podcasts, episodes'
      },
      {
        to: routesPaths.creatorAnalytics,
        label: 'Fans & CRM',
        note: 'Segments, churn risk, VIPs'
      },
      {
        to: routesPaths.creatorEvents,
        label: 'Live event center',
        note: 'Scheduled, PPV sell-through'
      },
      { to: routesPaths.creatorAds, label: 'Ads', note: 'Campaigns, sponsors, yield' },
      { to: routesPaths.creatorSettings, label: 'Settings', note: 'Profile, payouts, tax' }
    ]
  },
  {
    key: 'org',
    label: 'Organization / League',
    persona: 'Multi-team operating layer',
    status: 'In iteration',
    statusTone: 'iteration',
    tagline: 'Season operations, sponsorships, approvals, and governance.',
    summary:
      'Multi-team roster management, sponsor inventory, broadcast scheduling, revenue sharing, and white-label controls.',
    entryRoute: routesPaths.orgDashboard,
    screens: [
      { to: routesPaths.orgDashboard, label: 'Overview', note: 'Season health, sponsor fill' },
      { to: routesPaths.orgTeam, label: 'Teams & permissions', note: 'Rosters, roles, access' },
      {
        to: routesPaths.orgEvents,
        label: 'Event scheduling',
        note: 'Calendar, broadcast readiness'
      },
      {
        to: routesPaths.orgFinance,
        label: 'Revenue & settlements',
        note: 'Team payouts, sponsor receivables'
      },
      {
        to: routesPaths.orgAds,
        label: 'Sponsorship & ads',
        note: 'Patches, LED, broadcast, matchday'
      },
      { to: routesPaths.orgSettings, label: 'Settings & branding', note: 'White-label, domains' }
    ]
  },
  {
    key: 'admin',
    label: 'Platform Admin',
    persona: 'Operator / Trust & Safety',
    status: 'Design review',
    statusTone: 'design',
    tagline: 'Platform oversight, risk review, and operator controls.',
    summary:
      'Platform metrics, creator Connect ops, content moderation queues, reconciliation, fee config, and audit trails.',
    entryRoute: routesPaths.adminDashboard,
    screens: [
      { to: routesPaths.adminLogin, label: 'Admin login', note: 'Operator auth surface' },
      { to: routesPaths.adminDashboard, label: 'Dashboard', note: 'GMV, risk, Connect health' },
      { to: routesPaths.adminUsers, label: 'Users & creators', note: 'Search, verify, suspend' },
      {
        to: routesPaths.adminContent,
        label: 'Content moderation',
        note: 'Flags, takedowns, policies'
      },
      {
        to: routesPaths.adminFinance,
        label: 'Finance ops',
        note: 'Payouts, disputes, reconciliation'
      },
      {
        to: routesPaths.adminAds,
        label: 'Ad operations',
        note: 'House ads, network buys, boosts'
      },
      {
        to: routesPaths.adminSettings,
        label: 'Platform settings',
        note: 'Fees, webhooks, flags'
      }
    ]
  }
];

const UTILITIES = [
  {
    label: 'Legacy mocks',
    copy: 'Original standalone prototypes from before the architecture pass.',
    links: [
      { to: routesPaths.mockCreatorFinance, label: 'Creator mock' },
      { to: routesPaths.mockOrganizationDashboard, label: 'Org mock' }
    ]
  },
  {
    label: 'Feedback triage',
    copy: 'Review, tag, and triage every submission from the in-app widget.',
    links: [{ to: routesPaths.adminFeedback, label: 'Open triage queue' }]
  },
  {
    label: 'How to give feedback',
    copy:
      'Use the Feedback button in the bottom-right on any surface. Pick an element with the picker to tag a specific button, image, or section — the admin view can deep-link and flash it.',
    links: []
  }
];

const PreviewHomePage = () => {
  const history = useHistory();
  const [now, setNow] = useState(() => new Date());
  const [statusOverrides, setStatusOverrides] = useState(() => {
    const initial = {};
    for (const d of DASHBOARDS) {
      const stored = readStoredStatus(d.key);
      if (stored) initial[d.key] = stored;
    }
    return initial;
  });

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const buildTag = useMemo(() => {
    const env = (process.env.ENV || 'dev').toString();
    const date = now.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return `Build · ${env} · ${date}`;
  }, [now]);

  const handleStatusChange = (dashKey, nextValue) => {
    setStatusOverrides(prev => ({ ...prev, [dashKey]: nextValue }));
    writeStoredStatus(dashKey, nextValue);
  };

  // Resolve the effective status for a dashboard: stored override → default.
  const resolveStatus = dash => {
    const override = statusOverrides[dash.key];
    if (override) {
      const opt = STATUS_OPTIONS.find(o => o.value === override);
      return { tone: override, label: opt ? opt.label : dash.status };
    }
    return { tone: dash.statusTone, label: dash.status };
  };

  return (
    <div className="review-harness">
      <header className="review-harness__topbar">
        <div className="review-harness__brand">
          <Link to={routesPaths.home} className="review-harness__logo-link" aria-label="Identifi">
            <LogoIcon className="review-harness__logo" />
          </Link>
          <span className="review-harness__divider" aria-hidden="true" />
          <span className="review-harness__product">UI Review Harness</span>
        </div>
        <div className="review-harness__topbar-center">
          <span className="review-harness__pill">
            <span className="review-harness__pill-dot" />
            Internal preview — not for external distribution
          </span>
        </div>
        <div className="review-harness__topbar-right">
          <span className="review-harness__meta">{buildTag}</span>
          <Link to={routesPaths.adminFeedback} className="review-harness__topbar-link">
            Feedback triage
          </Link>
          <span className="review-harness__brand-mark" aria-hidden="true">
            <LogoIcon className="review-harness__logo review-harness__logo--small" />
          </span>
        </div>
      </header>

      <main className="review-harness__main">
        <section className="review-harness__hero">
          <span className="review-harness__eyebrow">Identifi · Internal Preview</span>
          <h1 className="review-harness__title">
            Review every Identifi surface, from one harness.
          </h1>
          <p className="review-harness__subtitle">
            Three operator dashboards, all screens wired with demo content. Jump into any surface,
            leave targeted feedback on any element, and track the triage queue — all without leaving
            this build.
          </p>
          <div className="review-harness__hero-actions">
            <button
              type="button"
              className="review-harness__cta review-harness__cta--primary"
              onClick={() => history.push(routesPaths.creatorDashboard)}
            >
              Start with Creator →
            </button>
            <button
              type="button"
              className="review-harness__cta"
              onClick={() => history.push(routesPaths.orgDashboard)}
            >
              Open Org
            </button>
            <button
              type="button"
              className="review-harness__cta"
              onClick={() => history.push(routesPaths.adminDashboard)}
            >
              Open Admin
            </button>
            <button
              type="button"
              className="review-harness__cta review-harness__cta--ghost"
              onClick={() => history.push(routesPaths.adminFeedback)}
            >
              Feedback triage
            </button>
          </div>
        </section>

        <section className="review-harness__grid" aria-label="Dashboards">
          {DASHBOARDS.map(dash => {
            const resolved = resolveStatus(dash);
            return (
            <article
              key={dash.key}
              className={`review-harness__card review-harness__card--${dash.key}`}
            >
              <div className="review-harness__card-head">
                <span className="review-harness__card-label">{dash.label}</span>
                <label
                  className={`review-harness__status review-harness__status--${resolved.tone} review-harness__status--editable`}
                  title="Click to change status"
                >
                  <span className="review-harness__status-dot" />
                  <span className="review-harness__status-label">{resolved.label}</span>
                  <span className="review-harness__status-caret" aria-hidden="true">▾</span>
                  <select
                    className="review-harness__status-select"
                    aria-label={`Change ${dash.label} status`}
                    value={resolved.tone}
                    onChange={e => handleStatusChange(dash.key, e.target.value)}
                  >
                    {STATUS_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <h2 className="review-harness__card-title">{dash.tagline}</h2>
              <p className="review-harness__card-persona">{dash.persona}</p>
              <p className="review-harness__card-copy">{dash.summary}</p>

              <div className="review-harness__screens" role="list">
                {dash.screens.map(screen => (
                  <Link
                    key={screen.to}
                    to={screen.to}
                    className="review-harness__screen"
                    role="listitem"
                  >
                    <span className="review-harness__screen-label">{screen.label}</span>
                    <span className="review-harness__screen-note">{screen.note}</span>
                    <span className="review-harness__screen-arrow" aria-hidden="true">
                      →
                    </span>
                  </Link>
                ))}
              </div>

              <div className="review-harness__card-foot">
                <Link to={dash.entryRoute} className="review-harness__card-cta">
                  Open {dash.label.toLowerCase()} →
                </Link>
              </div>
            </article>
            );
          })}
        </section>

        <section className="review-harness__utilities" aria-label="Review utilities">
          {UTILITIES.map(util => (
            <article key={util.label} className="review-harness__utility">
              <span className="review-harness__utility-label">{util.label}</span>
              <p className="review-harness__utility-copy">{util.copy}</p>
              {util.links.length > 0 && (
                <div className="review-harness__utility-links">
                  {util.links.map(link => (
                    <Link key={link.to} to={link.to} className="review-harness__utility-link">
                      {link.label} →
                    </Link>
                  ))}
                </div>
              )}
            </article>
          ))}
        </section>
      </main>

      <footer className="review-harness__footerlite">
        <span>Identifi · Internal preview</span>
        <span className="review-harness__footerlite-dot" aria-hidden="true">
          •
        </span>
        <span>Feedback widget lives bottom-right on every screen</span>
      </footer>
    </div>
  );
};

export default PreviewHomePage;
